/**
 * UserActionsContent
 *
 * Content component for user action sheet.
 * Displays a list of actions that can be performed on a user.
 *
 * Matches Flutter implementation: lib/presentation/bottom_sheet/user_actions_bottom_sheet.dart
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/presentation/store';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { useUserActions } from '../hooks/useUserActions';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { createUserActionHandlers } from './userActionHandlers';

export interface UserActionsContentProps {
  user?: UserEntity;
  userId?: string;
  screenKey?: string;
  /**
   * Forwarded to `useUserActions`. When `true` the current-user list includes
   * the owner account-management items. Set `true` only from the
   * `UserDetailsPage` app-bar entry.
   */
  includeAccountActions?: boolean;
}

export const UserActionsContent: React.FC<UserActionsContentProps> = ({
  user: initialUser,
  userId,
  screenKey,
  includeAccountActions,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const dispatch = useDispatch<AppDispatch>();

  // Use hook to manage user actions state and build the action list.
  const {
    user,
    userIsBlocked,
    loading,
    error,
    saveToFavorites,
    blockUser,
    userActions,
  } = useUserActions({ user: initialUser, userId, includeAccountActions });

  // Side-effecting action handlers (sheets, navigation, toasts, auth) live in a
  // colocated plain module rather than in `useUserActions` — the layer rule
  // keeps these presentation concerns out of the hook.
  const { handleActionClick } = createUserActionHandlers({
    user,
    screenKey,
    responsive,
    l10n,
    dispatch,
    saveToFavorites,
    blockUser,
    userIsBlocked,
  });

  // Show loading state while fetching user
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: responsive.spacing['3xl'],
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  // Show error state
  if (error || !user) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: responsive.spacing['3xl'],
          color: theme.text.secondary,
        }}
      >
        {l10n.errors.user.fetchFailed}
      </div>
    );
  }

  return (
    <div
      style={{
        paddingLeft: responsive.spacing.xl,
        paddingRight: responsive.spacing.xl,
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {userActions.map((action) => (
        <div
          key={action.type}
          {...qaAttr({ id: `user-action-sheet-${action.type}` })}
          style={{
            marginBottom: responsive.spacing['2xl'],
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => handleActionClick(action)}
        >
          <div
            style={{
              width: responsive.action.imageSize,
              height: responsive.action.imageSize,
              borderRadius: responsive.action.imageSize,
              backgroundColor: theme.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <AssetIcon path={action.image} width={responsive.action.imageSize / 2} height={responsive.action.imageSize / 2} />
          </div>
          <div
            style={{
              marginLeft: responsive.spacing.sm,
              flex: 1,
            }}
          >
            <span
              style={{
                color: theme.text.primary,
                fontSize: responsive.font.base,
              }}
            >
              {action.name}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
